import type {ClientTool, ClientToolCall} from '@github-ui/ahp-relay/relay-connection-runtime'
import {createHandoffStorage, type HandoffStorage} from '@github-ui/ahp-session-client/handoff-storage'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {fetchCAPI, FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {sendEvent} from '@github-ui/hydro-analytics'
import {repositoryPath} from '@github-ui/paths'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import type {ToolResultContentType, ToolCallResult} from '@microsoft/agent-host-protocol'

export interface PullRequestConfirmation {
  repoNwo: string
  title: string
  body: string
  head: string
  base: string
}

export interface PullRequestToolContext {
  repoNwo: string
  taskId: string
  sessionId: string
  environmentId: string
}

interface PullIdentity {
  id: number
  number: number
  repository_id: number
}

export interface PullRequestCreationRecord {
  phase: 'invoked' | 'creating' | 'created'
  repositoryId?: number
  pull?: PullIdentity
}

interface Dependencies {
  context: PullRequestToolContext
  apiURL?: string
  origin?: string
  storage?: Pick<HandoffStorage<PullRequestCreationRecord>, 'read' | 'write' | 'replace'>
}

const activeStates = new Set(['in_progress', 'idle', 'waiting_for_user'])
const pendingCreations = new Map<string, Promise<ToolCallResult>>()
const successfulCreations = new Map<string, PullRequestCreationRecord>()
// The exact parsed input (`{title, body, head, base}`, stringified) that produced each key's successful
// creation, so a later call with genuinely different arguments can be told apart from a recovery replay
// of the same call. Only covers a live tab — a reload loses this map along with `invokedKeys`, so recovery
// after a reload falls back to the lenient "trust the cached result" behavior below.
const originalInputs = new Map<string, string>()
// Once invoked (success or failure), every later call for the same context is refused by `fulfil` — the in-memory set covers a live tab, the sessionStorage marker it writes covers a reload.
const invokedKeys = new Set<string>()
const INVOKED_RECORD_ID = 'invoked'

function toolKey(context: PullRequestToolContext): string {
  const {repoNwo, taskId, sessionId, environmentId} = context
  return JSON.stringify([getCurrentUserLogin(), repoNwo, taskId, sessionId, environmentId])
}

export const CREATE_PULL_REQUEST_TOOL_DEFINITION: ClientTool['definition'] = {
  name: 'create_pull_request',
  title: 'Create a draft pull request',
  description:
    'Create a draft pull request for the current repository session. First prepare the changes and push the ' +
    'head branch to this repository. Do not invoke this tool until the push succeeds. The user is asked to ' +
    'confirm before the draft is created. Only active, repository-scoped CMC sessions support this prototype. ' +
    'This tool can only be used once per task session: on the first attempt, do not create the pull request ' +
    'through another tool. If it refuses because it was already used, fall back to another method instead ' +
    '(for example pushing the branch and running `gh pr create`).',
  inputSchema: {
    type: 'object',
    properties: {
      title: {type: 'string', description: 'Pull request title.'},
      body: {type: 'string', description: 'Pull request description.'},
      head: {type: 'string', description: 'Published head branch in the current repository, without an owner prefix.'},
      base: {type: 'string', description: 'Target branch in the current repository.'},
    },
    required: ['title', 'body', 'head', 'base'],
  },
  annotations: {readOnlyHint: false, destructiveHint: false, idempotentHint: false, openWorldHint: true},
  _meta: {'github.com/copilot:safeForTelemetry': {name: true, inputsNames: false}},
}

function isObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isPullIdentity(value: unknown): value is PullIdentity {
  return (
    isObject(value) &&
    [value.id, value.number, value.repository_id].every(
      field => typeof field === 'number' && Number.isSafeInteger(field) && field > 0,
    )
  )
}

function isCreationRecord(value: unknown): value is PullRequestCreationRecord {
  if (!isObject(value)) return false
  if (value.phase === 'invoked') return true
  return (
    typeof value.repositoryId === 'number' &&
    Number.isSafeInteger(value.repositoryId) &&
    value.repositoryId > 0 &&
    (value.phase === 'creating' ||
      (value.phase === 'created' && isPullIdentity(value.pull) && value.pull.repository_id === value.repositoryId))
  )
}

function isBranch(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    value.length > 0 &&
    value !== '@' &&
    !/[\s~^:?*[\]\\]/.test(value) &&
    [...value].every(character => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127) &&
    !value.includes('..') &&
    !value.includes('@{') &&
    !value.startsWith('-') &&
    !value.startsWith('refs/') &&
    !value.endsWith('.') &&
    value.split('/').every(part => part.length > 0 && !part.startsWith('.') && !part.endsWith('.lock'))
  )
}

function parseInput(call: ClientToolCall, repoNwo: string): PullRequestConfirmation {
  const input: unknown = JSON.parse(call.toolInput ?? '{}')
  if (
    !isObject(input) ||
    typeof input.title !== 'string' ||
    !input.title.trim() ||
    input.title.length > 256 ||
    typeof input.body !== 'string' ||
    input.body.length > 65_536 ||
    !isBranch(input.head) ||
    !isBranch(input.base) ||
    input.head === input.base ||
    Object.keys(input).some(key => !['title', 'body', 'head', 'base'].includes(key))
  ) {
    throw new Error('Provide a title, body, and different head and base branch names in the current repository.')
  }
  return {repoNwo, title: input.title.trim(), body: input.body, head: input.head, base: input.base}
}

/** Like {@link parseInput}, but returns `undefined` instead of throwing, so callers can tell "could not
 * prove a difference" apart from "the inputs differ" without conflating parse failures with real errors. */
function tryParseInput(call: ClientToolCall, repoNwo: string): string | undefined {
  try {
    return JSON.stringify(parseInput(call, repoNwo))
  } catch {
    return undefined
  }
}

async function verifyContext({context, apiURL}: Dependencies): Promise<number> {
  const {repoNwo, taskId, sessionId, environmentId} = context
  const response = await fetchCAPI({
    path: `/agents/repos/${repoNwo}/tasks/${encodeURIComponent(taskId)}`,
    copilotApiUrl: apiURL,
  })
  const data: unknown = await response.json()
  const task = isObject(data) && isObject(data.task) ? data.task : data
  const sessions =
    isObject(data) && Array.isArray(data.sessions) ? data.sessions : isObject(task) ? task.sessions : null
  if (!isObject(task) || task.id !== taskId || task.archived_at || !Array.isArray(sessions)) {
    throw new Error('This prototype requires an unarchived repository task with a matching CMC session.')
  }
  // The task may have had other sessions over its lifetime (reconnects, retries, follow-ups) —
  // we only require that *this* session is still the one actively driving the request.
  const session: unknown = sessions.find(candidate => isObject(candidate) && candidate.id === sessionId)
  const repository = task.repository
  if (
    !isObject(session) ||
    session.environment_id !== environmentId ||
    typeof session.state !== 'string' ||
    !activeStates.has(session.state) ||
    !isObject(repository) ||
    typeof repository.id !== 'number' ||
    !Number.isSafeInteger(repository.id) ||
    repository.id <= 0 ||
    (typeof task.nwo === 'string' && task.nwo.toLowerCase() !== repoNwo.toLowerCase())
  ) {
    throw new Error('The active AHP identity must match an active, repository-scoped CMC session on the task.')
  }
  return repository.id
}

async function verifyPublishedBranches({repoNwo, head, base}: PullRequestConfirmation): Promise<void> {
  const [owner = '', repo = ''] = repoNwo.split('/')
  const response = await verifiedFetchJSON(`${repositoryPath({owner, repo, action: 'refs'})}?type=branch`, {
    cache: 'no-store',
  })
  if (!response.ok) throw new Error(`Could not verify published branches (HTTP ${response.status}).`)
  const data: unknown = await response.json()
  if (!isObject(data) || !Array.isArray(data.refs) || !data.refs.includes(head) || !data.refs.includes(base)) {
    throw new Error('Both branches must exist in this repository. Push the head branch before creating the draft.')
  }
}

function completedResult(record: PullRequestCreationRecord, deps: Dependencies): ToolCallResult {
  const pull = record.pull
  if (!pull || pull.repository_id !== record.repositoryId)
    throw new Error('The saved pull request identity is invalid.')
  const url = new URL(`/${deps.context.repoNwo}/pull/${pull.number}`, deps.origin ?? window.location.origin).href
  return {
    success: true,
    pastTenseMessage: 'Created a draft pull request',
    structuredContent: {url, id: pull.id, number: pull.number, draft: true},
    content: [{type: 'text' as ToolResultContentType.Text, text: `Created draft pull request: ${url}`}],
  }
}

function recordId(value: PullRequestCreationRecord): string {
  return value.phase === 'invoked' ? INVOKED_RECORD_ID : String(value.repositoryId)
}

function defaultStorage(key: string): HandoffStorage<PullRequestCreationRecord> {
  return createHandoffStorage<PullRequestCreationRecord>({
    key: `github.create-pull-request.${key}`,
    payloadChars: () => 512,
    validate: isCreationRecord,
    getId: recordId,
  })
}

/**
 * Read-only check for UI display purposes only (e.g. disabling a stale retry trigger after the
 * tool has already been used once). This does NOT enforce anything — `fulfil` above is the sole
 * authority for refusing reuse; this is just a best-effort hint for the UI to avoid offering a
 * dead-end retry.
 */
export function isCreatePullRequestConsumed(context: PullRequestToolContext): boolean {
  const key = toolKey(context)
  if (invokedKeys.has(key)) return true
  const reader = createHandoffStorage<PullRequestCreationRecord>({
    key: `github.create-pull-request.${key}`,
    payloadChars: () => 512,
    validate: isCreationRecord,
  })
  return reader.read() !== null
}

export function buildCreatePullRequestClientTool(deps: Dependencies): ClientTool {
  const key = toolKey(deps.context)
  const storage = deps.storage ?? defaultStorage(key)
  return {
    definition: CREATE_PULL_REQUEST_TOOL_DEFINITION,
    fulfil: call => {
      // Sampled before this call's own marking below, so it only reflects an *earlier* call.
      const alreadyInvoked = invokedKeys.has(key) || storage.read() !== null
      invokedKeys.add(key)
      // Best-effort: only mark "invoked" if nothing is stored yet, so this never clobbers a `creating`/`created` record from an earlier call.
      if (!storage.read()) storage.write({phase: 'invoked'})
      const pending = pendingCreations.get(key)
      if (pending) return pending
      const result = fulfil(call, deps, key, storage, alreadyInvoked).finally(() => pendingCreations.delete(key))
      pendingCreations.set(key, result)
      return result
    },
  }
}

const ALREADY_CALLED_MESSAGE =
  'This tool was already called once for this task session and cannot be used again. ' +
  'Create the pull request a different way instead (for example by pushing the branch and running `gh pr create`).'

async function fulfil(
  call: ClientToolCall,
  deps: Dependencies,
  key: string,
  storage: Pick<HandoffStorage<PullRequestCreationRecord>, 'read' | 'write' | 'replace'>,
  alreadyInvoked: boolean,
): Promise<ToolCallResult> {
  let persisted = false
  let creationStarted = false
  try {
    const saved = successfulCreations.get(key) ?? storage.read()
    if (saved?.phase === 'created') {
      // Only refuse a differing call if we can actually prove it differs: a missing stored original
      // (e.g. after a reload, when only sessionStorage — not this in-memory map — survives) or a new
      // call whose input doesn't even parse can't be compared, so both fall back to the lenient,
      // pre-existing "trust the cached result" recovery behavior. A narrow gap remains: two truly
      // concurrent calls with different input can still be deduped into one result by
      // `pendingCreations` below before either is persisted; that's an accepted, documented edge case.
      const storedInput = originalInputs.get(key)
      const newInput = storedInput === undefined ? undefined : tryParseInput(call, deps.context.repoNwo)
      if (storedInput !== undefined && newInput !== undefined && newInput !== storedInput) {
        throw new Error(ALREADY_CALLED_MESSAGE)
      }
      return completedResult(saved, deps)
    }
    if (saved?.phase === 'creating') {
      throw new Error(
        "A previous creation may have succeeded. Check the task's pull request before trying again; do not retry automatically.",
      )
    }
    // `saved` can only be undefined or `{phase: 'invoked'}` here; this prototype doesn't support retrying a failed attempt or creating a second PR.
    if (alreadyInvoked) {
      throw new Error(ALREADY_CALLED_MESSAGE)
    }
    const {context} = deps
    if (
      !/^[a-zA-Z0-9-]+\/[a-zA-Z0-9_.-]+$/.test(context.repoNwo) ||
      context.repoNwo.split('/').some(part => part === '.' || part === '..') ||
      !context.taskId ||
      !context.sessionId ||
      !context.environmentId
    ) {
      throw new Error(
        'Pull request creation requires a trusted repository, task, CMC session and environment identity.',
      )
    }
    const input = parseInput(call, context.repoNwo)
    const repositoryId = await verifyContext(deps)
    await verifyPublishedBranches(input)
    await fetchCAPI({
      path: `/agents/sessions/${encodeURIComponent(context.sessionId)}`,
      copilotApiUrl: deps.apiURL,
      method: 'PUT',
      body: {
        head_ref: input.head,
        base_ref: input.base,
        generated_pull_data: {title: input.title, description: input.body},
      },
    })
    persisted = true
    if ((await verifyContext(deps)) !== repositoryId) throw new Error('The task repository changed before PR creation.')
    // `replace` rather than `write`: `write` clears the existing record before setting the new one, so
    // a `setItem` failure in between (e.g. a quota error) would silently drop the `invoked` marker it
    // just removed. `replace` only ever issues a single `setItem`, so a failure here leaves the prior
    // `invoked` marker intact instead of losing the single-use guard on reload.
    const storageFailure = storage.replace(INVOKED_RECORD_ID, {phase: 'creating', repositoryId})
    if (storageFailure)
      throw new Error(`Cannot preserve PR creation recovery state (${storageFailure}); creation did not start.`)
    creationStarted = true
    sendEvent('copilot.agent_session.pr_creation_requested', {nwo: context.repoNwo, task_id: context.taskId})
    const response = await fetchCAPI({
      path: `/agents/repos/${context.repoNwo}/tasks/${encodeURIComponent(context.taskId)}/pulls`,
      copilotApiUrl: deps.apiURL,
      method: 'POST',
      retryOn401: false,
    })
    const pull: unknown = await response.json()
    if (!isPullIdentity(pull) || pull.repository_id !== repositoryId) {
      throw new Error('Creation returned an invalid pull request identity. Check the task before trying again.')
    }
    const record: PullRequestCreationRecord = {phase: 'created', repositoryId, pull}
    successfulCreations.set(key, record)
    originalInputs.set(key, JSON.stringify(input))
    const saveFailure = storage.replace(String(repositoryId), record)
    const result = completedResult(record, deps)
    sendEvent('copilot.agent_session.pr_creation_completed', {
      nwo: context.repoNwo,
      task_id: context.taskId,
      pr_id: pull.id,
      pr_number: pull.number,
    })
    if (saveFailure) {
      result.content?.push({
        type: 'text' as ToolResultContentType.Text,
        text: `The PR exists, but the browser could not save its recovery result (${saveFailure}). Keep the URL; do not create another PR after reloading.`,
      })
    }
    return result
  } catch (error) {
    // `rawMessage` can carry untrusted text from an upstream HTTP response (FetchCAPIError's
    // message is parsed from the response body) — never surface that to the agent as tool
    // output (tool output poisoning). Our own thrown errors are static, developer-authored
    // strings with no interpolated untrusted content, so they stay safe to show verbatim.
    const rawMessage = error instanceof Error ? error.message : String(error)
    const message = error instanceof FetchCAPIError ? 'The request to create the pull request failed.' : rawMessage
    if (creationStarted) {
      sendEvent('copilot.agent_session.pr_creation_failed', {
        nwo: deps.context.repoNwo,
        task_id: deps.context.taskId,
        status: error instanceof FetchCAPIError ? error.status : undefined,
        error: rawMessage,
      })
    }
    const outcome = creationStarted
      ? 'CMC saved the PR metadata and creation may have succeeded. Do not retry automatically.'
      : persisted
        ? 'CMC saved the PR metadata, but PR creation did not start.'
        : 'No PR creation request was sent.'
    return {
      success: false,
      pastTenseMessage: 'Could not create a draft pull request',
      error: {message},
      content: [{type: 'text' as ToolResultContentType.Text, text: `${message} ${outcome}`}],
    }
  }
}
